import type React from "react";

import { LoadingPanel } from "@/components/ui/loading-state";

interface AsyncBoundaryProps<T> {
  /**
   * A Convex `useQuery` result (or anything with the same three-state shape):
   * `undefined` while loading, `null` when the thing does not exist, the value otherwise.
   */
  data: T | null | undefined;
  /** Rendered while `data` is `undefined`. Defaults to a centered {@link LoadingPanel}. */
  loading?: React.ReactNode;
  /** Rendered when `data` is `null`. Without it a `null` result renders nothing. */
  notFound?: React.ReactNode;
  /** Rendered when the resolved data is empty — by default, an empty array. */
  empty?: React.ReactNode;
  /**
   * Overrides the emptiness test for data whose list is nested
   * (`(page) => page.rows.length === 0`). Only consulted when `empty` is given.
   */
  isEmpty?: (data: NonNullable<T>) => boolean;
  /** Rendered with the resolved, non-null data. */
  children: (data: NonNullable<T>) => React.ReactNode;
}

function isDefaultEmpty(data: unknown) {
  return Array.isArray(data) && data.length === 0;
}

/**
 * AsyncBoundary - the one place the loading / not-found / empty / loaded ladder is written.
 *
 * Use it wherever a query result is consumed directly, so the four branches are decided once
 * instead of re-ternaried per screen:
 *
 * ```tsx
 * <AsyncBoundary
 *   data={assignments}
 *   loading={<LoadingPanel className="min-h-16" />}
 *   empty={<p className="px-5 py-6 text-center text-[13px]">No organizations assigned yet.</p>}
 * >
 *   {(assignments) => <AssignmentsTable rows={assignments} />}
 * </AsyncBoundary>
 * ```
 *
 * A page whose loaded branch is the whole component body still reads better as early returns —
 * reach for `LoadingPanel` / `LoadingState` / `StatusPanel` directly there.
 */
function AsyncBoundary<T>({
  data,
  loading,
  notFound,
  empty,
  isEmpty,
  children,
}: AsyncBoundaryProps<T>) {
  if (data === undefined) {
    return <>{loading ?? <LoadingPanel />}</>;
  }
  if (data === null) {
    return <>{notFound ?? null}</>;
  }
  const resolved = data as NonNullable<T>;
  if (empty !== undefined && (isEmpty ? isEmpty(resolved) : isDefaultEmpty(resolved))) {
    return <>{empty}</>;
  }
  return <>{children(resolved)}</>;
}

export { AsyncBoundary };
